import {memo, ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {IWorkflowPageProps} from "../../logic/workflows";
import {BudgetingType} from "../../../../BudgetingType";
import {
    GetUtsvOperationalMetricsForPropertyYearQuery, UnitTypeScheduleValueUpsertInput,
    useBatchUpsertUnitTypeScheduleValuesMutation,
    useGetForecastLocksLazyQuery,
    useGetUtsvOperationalMetricsForPropertyYearLazyQuery, VersionType
} from "../../../../__generated__/generated_types";
import {getBudgetYearForProperty, useProperties} from "../../../../contexts/properties/PropertiesContext";
import {AuthContext} from "../../../../contexts/AuthContext";
import {useParams} from "react-router-dom";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";
import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import VZSaveStatus from "../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import {Tabs} from "@zendeskgarden/react-tabs";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET,
    WORKFLOW_YEAR_TAB_REFORECAST
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {getMonths} from "../../../../utils/months-helper";
import {OperationalFinancialMetrics} from "../operational-metrics";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import {
    CompoundSimpleHotRow, SimpleHoTCellType,
    SimpleHoTChange,
    SimpleHoTWrapper
} from "../../../admin/line-item-management/SimpleHoTWrapper";
import {useVersions} from "../../../../contexts/versions/VersionsContext";
import {toast} from "react-toastify";

type MetricDataType = GetUtsvOperationalMetricsForPropertyYearQuery["originalRevenueUTSVOperationalMetricsForPropertyYear"][0]["unitTypes"][0];
const COL_TO_MONTH: Record<string, number> = {
    "values.0": 0,
    "values.1": 1,
    "values.2": 2,
    "values.3": 3,
    "values.4": 4,
    "values.5": 5,
    "values.6": 6,
    "values.7": 7,
    "values.8": 8,
    "values.9": 9,
    "values.10": 10,
    "values.11": 11
};

function asNumber(value: SimpleHoTCellType): number | null {
    if(value === null || typeof value === "number") {
        return value;
    }

    if(typeof value === "string") {
        const val = parseFloat(value);
        return isNaN(val) ? null : val;
    }

    return null;
}

function asDecimalString(value: SimpleHoTCellType): string | null {
    if(value === null || typeof value === "number") {
        return value === null ? null : value.toString(10);
    }

    if(typeof value === "string") {
        const val = parseFloat(value);
        return isNaN(val) ? null : val.toString(10);
    }
    return null;
}

export function AdminModelDown(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType;};
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();

    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);

    const {versions} = useVersions();

    const versionIdToSave = useMemo(() => {
        const rfcstVersionId = versions?.valueVersions.find(x => x.year == budgetYear - 1 && x.type == VersionType.Reforecast)?.id;
        const bdgtVersionId = versions?.valueVersions.find(x => x.year == budgetYear && x.type == VersionType.Budget)?.id;
        return versionType === VersionType.Reforecast ? rfcstVersionId : bdgtVersionId;
    }, [versions, budgetYear, versionType]);

    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [selectedTab, setSelectedTab] = useState<string>(versionType === VersionType.Budget ? WORKFLOW_YEAR_TAB_BUDGET : WORKFLOW_YEAR_TAB_REFORECAST);


    const [getForecastLocks, {data: forecastLocksData, loading: forecastLocksDataLoading}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [getMetricData, {data: metricData, loading: metricDataLoading}] = useGetUtsvOperationalMetricsForPropertyYearLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [upsertUnitTypeScheduleValues, {loading: savingValues}] = useBatchUpsertUnitTypeScheduleValuesMutation({
        fetchPolicy: "no-cache"
    });

    const [simplifiedMetricData, setSimplifiedMetricData] = useState<Record<string, MetricDataType> | undefined>(undefined);
    const [collectedChanges, setCollectedChanges] = useState<SimpleHoTChange[]>([]);

    useEffect(() => {
        if(!currentProperty) {
            return;
        }


        setCollectedChanges([]);
        getForecastLocks({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: budgetYear
            }
        });
        getMetricData({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: budgetYear
            }
        });
    }, [currentProperty]);

    useEffect(() => {
        if(savingValues || collectedChanges.length === 0 || !currentProperty || !versionIdToSave) {
            return;
        }

        // Collapse the changes so we only have one change for each thing.
        const collapsed: Record<string, SimpleHoTChange> = {};
        for(const change of collectedChanges) {
            const key = `${change.rowId}-${change.col}`;
            const val = collapsed[key];
            if(val === undefined) {
                collapsed[key] = {
                    ...change
                };
            } else {
                val.newValue = change.newValue;
            }
        }

        const byFieldName: Record<string, UnitTypeScheduleValueUpsertInput[]> = {};
        for(const change of Object.values(collapsed)) {
            const valuesIdx = change.compoundRowValuesIndex;
            const month = COL_TO_MONTH[change.col];
            if(change.row.isCompound && typeof valuesIdx !== "undefined" && typeof month !== "undefined") {
                const row = change.row as CompoundSimpleHotRow;
                const values = row.values[valuesIdx];
                if(values) {
                    const field = values["fieldName"];
                    const unitTypeName = values["unitTypeName"];
                    if(typeof unitTypeName === "string" && typeof field === "string") {
                        const existing = byFieldName[field];
                        const val: UnitTypeScheduleValueUpsertInput = {
                            unitTypeName: unitTypeName,
                            monthIndex: month,
                            value: asDecimalString(change.newValue),
                        };
                        if(!existing) {
                            byFieldName[field] = [val];
                        } else {
                            existing.push(val);
                        }
                    }
                }
            }
        }

        setCollectedChanges([]);
        upsertUnitTypeScheduleValues({
            variables: {
                batchArgs: Object.entries(byFieldName).map(([field, values]) => ({
                    propertyId: currentProperty.id,
                    versionId: versionIdToSave,
                    fieldName: field,
                    values: values,
                    budgetYear: budgetYear,
                }))
            }
        }).then(res => {
            if(res.data?.batchUpsertUnitTypeScheduleValues) {
                setLastSaved(new Date());
                return;
            }
            toast.error("Failed to update Admin/Model/Down units.");
        }).catch(() => {
            toast.error("Failed to update Admin/Model/Down units.");
        });

    }, [collectedChanges, savingValues]);

    useEffect(() => {
        if(!metricData || metricDataLoading) {
            return;
        }

        let year = budgetYear;
        if(versionType === VersionType.Reforecast) {
            year -= 1;
        }

        const requestedData = metricData.originalRevenueUTSVOperationalMetricsForPropertyYear.find(d => d.year === year && d.version === versionType);
        if(!requestedData) {
            setSimplifiedMetricData({});
            return;
        }

        setSimplifiedMetricData(requestedData.unitTypes.toIdMap(ut => ut.unitTypeId));
    }, [metricData, metricDataLoading]);

    const months = getMonths(currentProperty);
    const tableData = useMemo(() => {
        const rows: CompoundSimpleHotRow[] = [];
        if(simplifiedMetricData === undefined) {
            return rows;
        }

        const sorted = Object.values(simplifiedMetricData).sortBy(a => a.unitTypeName);
        for(const ut of sorted) {
            const vacantCount: number[] = new Array(12).fill(0);
            const totalCount: number[] = new Array(12).fill(0);
            const unitCount = ut.unitTypeCount;
            const adminCount = ut.adminUnitCount.map(n => typeof n !== "number" ? 0 : n);
            const modelCount = ut.modelUnitCount.map(n => typeof n !== "number" ? 0 : n);
            const downCount = ut.downUnitCount.map(n => typeof n !== "number" ? 0 : n);

            for(let i = 0; i < 12; i++) {
                const occupancyPercentage = ut.occupancyRate[i] ?? 0;
                vacantCount[i] += Math.floor(unitCount * (1 - occupancyPercentage));
                totalCount[i] += (adminCount[i]! + modelCount[i]! + downCount[i]!);
            }

            rows.push({
                isCompound: true,
                verticalColumnMerges: [0],
                values: [
                    {
                        label: ut.unitTypeName,
                        subLabel: "Model Unit Count",
                        values: modelCount,
                        unitTypeId: ut.unitTypeId,
                        fieldName: "modelUnitCount",
                        unitTypeName: ut.unitTypeName
                    },
                    {
                        label: ut.unitTypeName,
                        subLabel: "Down Unit Count",
                        values: downCount,
                        unitTypeId: ut.unitTypeId,
                        fieldName: "downUnitCount",
                        unitTypeName: ut.unitTypeName
                    },
                    {
                        label: ut.unitTypeName,
                        subLabel: "Admin Unit Count",
                        values: adminCount,
                        unitTypeId: ut.unitTypeId,
                        fieldName: "adminUnitCount",
                        unitTypeName: ut.unitTypeName
                    },
                    {
                        label: ut.unitTypeName,
                        subLabel: "Total",
                        values: totalCount
                    },
                    {
                        label: ut.unitTypeName,
                        subLabel: "Vacant Count",
                        values: vacantCount
                    }
                ]
            });
        }

        return rows;
    }, [simplifiedMetricData]);

    function updateData(changes: SimpleHoTChange[]): void {
        if(changes.length === 0) {
            return;
        }

        const updates = {...simplifiedMetricData};

        for(const change of changes) {
            const valuesIdx = change.compoundRowValuesIndex;
            const month = COL_TO_MONTH[change.col];
            if(change.row.isCompound && typeof valuesIdx !== "undefined" && typeof month !== "undefined") {
                const row = change.row as CompoundSimpleHotRow;
                const values = row.values[valuesIdx];
                if(values) {
                    const unitTypeId = values["unitTypeId"];
                    const field = values["fieldName"];
                    if(typeof unitTypeId === "string" && typeof field === "string") {
                        const metricData = updates[unitTypeId];
                        if(metricData) {
                            switch(field) {
                                case "adminUnitCount":
                                    metricData.adminUnitCount[month] = asNumber(change.newValue);
                                    break;
                                case "modelUnitCount":
                                    metricData.modelUnitCount[month] = asNumber(change.newValue);
                                    break;
                                case "downUnitCount":
                                    metricData.downUnitCount[month] = asNumber(change.newValue);
                                    break;
                            }
                        }
                    }
                }
            }
        }

        setSimplifiedMetricData(updates);
    }

    return <ThemeProvider theme={ViziblyTheme}>
        <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
        <WorkflowHeader>
            <WorkflowHeader.LeftCol>
            <div className={workflowHeaderCSS.rowItemsContainer}>
                    <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
            <WorkflowPageLabel label="Admin, Model, and Down Units" />
        </div>
        </WorkflowHeader.LeftCol>
            <WorkflowHeader.RightCol>
                <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
            </WorkflowHeader.RightCol>
        </WorkflowHeader>

            <div className={workflowCSS.bodyContainer}>
                {currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                        property={currentProperty}
                                        selectedTab={selectedTab}
                                        rawForecastLocks={forecastLocksData}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <MemoizedSimpleHoTWrapper
                                        columns={[
                                            {data: "label"},
                                            {data: "subLabel"},
                                            {data: `values.0`, type: "numeric"},
                                            {data: `values.1`, type: "numeric"},
                                            {data: `values.2`, type: "numeric"},
                                            {data: `values.3`, type: "numeric"},
                                            {data: `values.4`, type: "numeric"},
                                            {data: `values.5`, type: "numeric"},
                                            {data: `values.6`, type: "numeric"},
                                            {data: `values.7`, type: "numeric"},
                                            {data: `values.8`, type: "numeric"},
                                            {data: `values.9`, type: "numeric"},
                                            {data: `values.10`, type: "numeric"},
                                            {data: `values.11`, type: "numeric"}
                                        ]}
                                        rows={tableData}
                                        dataLoading={metricDataLoading || forecastLocksDataLoading}
                                        hotProps={{
                                            renderAllRows: true,
                                            height: "60vh",
                                            colHeaders: ["Unit Type", "Metric", ...months],
                                            colWidths: [300, 168, ...(new Array(12).fill(113))],
                                            fixedColumnsStart: 2,
                                            autoRowSize: false,
                                            autoColumnSize: false,
                                            rowHeights: 40,
                                            undo: false
                                        }}
                                        cellStyler={(_row, col, rowData, _cellValue, compoundRowValuesIndex) => {
                                            let readOnlyRow = false;
                                            if(compoundRowValuesIndex) {
                                                const subRow = (rowData as CompoundSimpleHotRow).values[compoundRowValuesIndex];
                                                readOnlyRow = subRow?.["subLabel"] === "Total" || subRow?.["subLabel"] === "Vacant Count";
                                            }

                                            if(currentProperty && versionType === VersionType.Reforecast) {
                                                readOnlyRow = readOnlyRow || (col - 2) < currentProperty.reforecastStartMonthIndex;
                                            }

                                            if(col < 2 || col === 14 || readOnlyRow) {
                                                return {
                                                    isReadOnly: true
                                                };
                                            }
                                            return {};
                                        }}
                                        onChange={changes => {
                                            updateData(changes);
                                            // This concat order is important for collapsing the changes later before
                                            // attempting to write the save data.
                                            setCollectedChanges(collectedChanges.concat(changes));
                                        }}
                                />
                                {!currentProperty?.isAcquisitionProperty &&
                                        <OperationalFinancialMetrics type={type} budgetYear={budgetYear} />
                                }
                            </div>
                        </>
                }
            </div>
        </div>
    </ThemeProvider>;
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    return prevProps.rows === nextProps.rows || prevProps.dataLoading !== nextProps.dataLoading;
});